import React, { useState, useEffect } from "react";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { usePaymentApi } from "../hooks/use-payment-api";
import { Input } from "../../../../shadcn/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../../shadcn/ui/select";
import { Button } from "../../../../shadcn/ui/button";
import { Label } from "../../../../shadcn/ui/label";
import { PhoneInput } from "../../../../components/ui/phone-input";
import messageHandler from "../../../../utils/messageHandler";
import logger from "../../../../utils/logger";


const BankAccountForm = ({ onPrevious, onComplete, currency, country }) => {
  const { loading, error, fetchPaymentInstitutions, fetchAccountTypes, submitPaymentSetup } = usePaymentApi();
  const [paymentInstitutions, setPaymentInstitutions] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [formData, setFormData] = useState({
    paymentMethod: "bank",
    paymentInstitutionId: "",
    accountType: "",
    accountNumber: "",
    accountName: "",
    branchCode: "",
    swiftCode: "",
    businessName: "",
    businessPhoneNumber: "",
    businessEmailAddress: "",
    contactPersonName: "",
    contactPersonPhone: "",
    contactPersonEmail: "",
  });

  useEffect(() => {
    const loadData = async () => {
      try {
        if (!currency || !country) return;

        const [institutionsData, accountTypesData] = await Promise.all([
          fetchPaymentInstitutions(currency, country),
          fetchAccountTypes(),
        ]);

        if (institutionsData && Array.isArray(institutionsData)) {
          setPaymentInstitutions(institutionsData);
        }
        if (accountTypesData && Array.isArray(accountTypesData)) {
          setAccountTypes(accountTypesData);
        }
      } catch (error) {
        messageHandler.showApiMessage(error, "load payment data");
      }
    };

    loadData();
  }, []); // Remove dependencies to prevent continuous calls

  const handleSubmit = async () => {
    try {
      // Get selected institution to get its code
      const selectedInstitution = paymentInstitutions.find(
        inst => inst.id.toString() === formData.paymentInstitutionId
      );

      // Add paymentInstitutionCode and primary to payload
      const payload = {
        ...formData,
        paymentInstitutionCode: selectedInstitution?.code || "",
        primary: true, // Hardcoded to true
      };

      logger.log("Form data being submitted:", payload);
      logger.log("accountType value:", formData.accountType);
      logger.log("accountType type:", typeof formData.accountType);
      const result = await submitPaymentSetup(payload);
      if (result) {
        messageHandler.showApiMessage(result, "payment setup");
        onComplete();
      } else {
        // Handle case where result is null but no error was thrown
        messageHandler.showApiMessage({ message: "Payment setup failed" }, "payment setup");
      }
    } catch (error) {
      logger.log("Caught error in handleSubmit:", error);
      logger.log("Calling messageHandler.showApiMessage with:", error, "payment setup");
      messageHandler.showApiMessage(error, "payment setup");
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border">
      {/* Header */}
      <div className="px-6 py-4 border-b">
        <h1 className="text-xl font-semibold">Payment Setup</h1>
        <div className="w-full bg-gray-200 h-1 mt-2">
          <div className="bg-black h-1 w-full"></div>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="mb-6">
          <h2 className="text-lg font-medium mb-2">Payment Setup</h2>
          <p className="text-gray-600">Set up your payment method for receiving payments</p>
        </div>

        <div className="mb-6">
          <h3 className="font-medium mb-2">Payment Method</h3>
          <p className="text-gray-600 mb-4">
            Select how you would like to receive payments. This will be synced with our internal system.
          </p>

          {/* Selected Method Display */}
          <div className="border rounded-lg p-4 bg-blue-50 mb-6">
            <div className="flex items-center">
              <input type="radio" checked readOnly className="mr-3" />
              <div className="flex items-center">
                <div className="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                  <div className="w-4 h-4 bg-blue-500 rounded"></div>
                </div>
                <div>
                  <div className="font-medium">Bank Account</div>
                  <div className="text-sm text-gray-600">Receive payments directly to your bank account</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bank Account Details */}
        <div className="mb-6">
          <h3 className="font-medium mb-2">Bank Account Details</h3>
          <p className="text-gray-600 mb-4">
            Please provide your complete bank account information for payment processing
          </p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 text-blue-600 mr-2 mt-0.5" />
              <p className="text-sm text-blue-800">
                Ensure all payment institution details are accurate to avoid payment delays or failures.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block text-sm font-medium mb-2">
                Payment Institution <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.paymentInstitutionId}
                onValueChange={value => handleInputChange("paymentInstitutionId", value)}
                disabled={loading}
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={loading ? "Loading payment institutions..." : "Select payment institution"}
                  />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(paymentInstitutions) &&
                    paymentInstitutions.map(bank => (
                      <SelectItem key={bank.id} value={bank.id.toString()}>
                        {bank.name} ({bank.shortName})
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="block text-sm font-medium mb-2">
                Account Type <span className="text-red-500">*</span>
              </Label>
              <Select value={formData.accountType} onValueChange={value => handleInputChange("accountType", value)}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select account type" />
                </SelectTrigger>
                <SelectContent>
                  {Array.isArray(accountTypes) &&
                    accountTypes.map(type => (
                      <SelectItem key={type.id} value={type.id.toString()}>
                        {type.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <Label className="block text-sm font-medium mb-2">
                Account Number <span className="text-red-500">*</span>
              </Label>
              <Input
                type="text"
                placeholder="Enter account number"
                className="w-full"
                value={formData.accountNumber}
                onChange={e => handleInputChange("accountNumber", e.target.value)}
              />
            </div>
            <div>
              <Label className="block text-sm font-medium mb-2">Branch Code</Label>
              <Input
                type="text"
                placeholder="Enter branch code"
                className="w-full"
                value={formData.branchCode}
                onChange={e => handleInputChange("branchCode", e.target.value)}
              />
            </div>
          </div>

          <div className="mb-4">
            <Label className="block text-sm font-medium mb-2">
              Account Name <span className="text-red-500">*</span>
            </Label>
            <Input
              type="text"
              placeholder="Enter account holder name"
              className="w-full"
              value={formData.accountName}
              onChange={e => handleInputChange("accountName", e.target.value)}
            />
          </div>

          <div className="mb-6">
            <Label className="block text-sm font-medium mb-2">SWIFT Code (for international transfers)</Label>
            <Input
              type="text"
              placeholder="Enter SWIFT/BIC code"
              className="w-full"
              value={formData.swiftCode}
              onChange={e => handleInputChange("swiftCode", e.target.value)}
            />
          </div>

          {/* Contact Information */}
          <div className="mb-6">
            <h3 className="font-medium mb-4">Contact Information</h3>
            <p className="text-gray-600 mb-4">Please provide your business contact details for payment processing</p>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Business Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter business name"
                  className="w-full"
                  value={formData.businessName}
                  onChange={e => handleInputChange("businessName", e.target.value)}
                />
              </div>
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Business Phone <span className="text-red-500">*</span>
                </Label>
                <PhoneInput
                  defaultCountry="ke"
                  value={formData.businessPhoneNumber}
                  onChange={value => handleInputChange("businessPhoneNumber", value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Business Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  placeholder="Enter business email"
                  className="w-full"
                  value={formData.businessEmailAddress}
                  onChange={e => handleInputChange("businessEmailAddress", e.target.value)}
                />
              </div>
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Contact Person Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="text"
                  placeholder="Enter contact person name"
                  className="w-full"
                  value={formData.contactPersonName}
                  onChange={e => handleInputChange("contactPersonName", e.target.value)}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Contact Person Phone <span className="text-red-500">*</span>
                </Label>
                <PhoneInput
                  defaultCountry="ke"
                  value={formData.contactPersonPhone}
                  onChange={value => handleInputChange("contactPersonPhone", value)}
                />
              </div>
              <div>
                <Label className="block text-sm font-medium mb-2">
                  Contact Person Email <span className="text-red-500">*</span>
                </Label>
                <Input
                  type="email"
                  placeholder="Enter contact person email"
                  className="w-full"
                  value={formData.contactPersonEmail}
                  onChange={e => handleInputChange("contactPersonEmail", e.target.value)}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-between">
          <Button variant="ghost" onClick={onPrevious} className="flex items-center text-gray-600 hover:text-gray-800">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Previous
          </Button>
          <Button onClick={handleSubmit} disabled={loading} className="flex items-center">
            {loading ? "Processing..." : "Complete Setup"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BankAccountForm;
